// Chunk 86: Enterprise & White-Label Features — Organisation API client
import apiClient from './client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Organisation {
  id: string;
  name: string;
  ownerUserId: string;
  plan: 'firm' | 'enterprise';
  maxMembers: number;
  customDomain: string | null;
  createdAt: string;
}

export interface OrgMembership extends Organisation {
  yourRole: 'owner' | 'admin' | 'member';
}

export interface OrgMember {
  userId: string;
  role: 'owner' | 'admin' | 'member';
  joinedAt: string;
  email: string;
  displayName: string;
}

export interface AssetWeight {
  symbol: string;
  name: string;
  valueUsd: number;
  weight: number;
}

export interface MemberSummary {
  userId: string;
  role: string;
  portfolioValueUsd: number | null;
  assetCount: number;
}

export interface OrgAggregate {
  orgId: string;
  memberCount: number;
  aggregateValue: number;
  currency: string;
  topAssets: AssetWeight[];
  consolidatedRiskScore: number;
  memberSummaries: MemberSummary[];
  computedAt: string;
}

// ─── API functions ────────────────────────────────────────────────────────────

export async function createOrganisation(
  name: string,
  plan: 'firm' | 'enterprise' = 'firm',
): Promise<{ organisation: Organisation }> {
  const res = await apiClient.post<{ organisation: Organisation }>('/auth/org/create', {
    name,
    plan,
  });
  return res.data;
}

export async function getMyOrganisations(): Promise<{ organisations: OrgMembership[] }> {
  const res = await apiClient.get<{ organisations: OrgMembership[] }>('/auth/org/my/memberships');
  return res.data;
}

export async function getOrganisation(
  orgId: string,
): Promise<{ organisation: Organisation; yourRole: string }> {
  const res = await apiClient.get<{ organisation: Organisation; yourRole: string }>(
    `/auth/org/${orgId}`,
  );
  return res.data;
}

export async function getOrgMembers(orgId: string): Promise<{ members: OrgMember[] }> {
  const res = await apiClient.get<{ members: OrgMember[] }>(`/auth/org/${orgId}/members`);
  return res.data;
}

export async function inviteMember(
  orgId: string,
  email: string,
  role: 'admin' | 'member' = 'member',
): Promise<{ message: string; inviteLink: string; inviteToken?: string }> {
  const res = await apiClient.post<{
    message: string;
    inviteLink: string;
    inviteToken?: string;
  }>('/auth/org/invite', { orgId, email, role });
  return res.data;
}

export async function acceptInvite(token: string): Promise<{
  message: string;
  orgId: string;
  role: string;
}> {
  const res = await apiClient.post<{ message: string; orgId: string; role: string }>(
    '/auth/org/accept-invite',
    { token },
  );
  return res.data;
}

export async function removeMember(orgId: string, memberId: string): Promise<void> {
  await apiClient.delete(`/auth/org/${orgId}/members/${memberId}`);
}

export async function updateOrganisation(
  orgId: string,
  updates: { name?: string; customDomain?: string | null },
): Promise<{ organisation: Organisation }> {
  const res = await apiClient.patch<{ organisation: Organisation }>(`/auth/org/${orgId}`, updates);
  return res.data;
}

export async function getOrgAggregate(orgId: string): Promise<OrgAggregate> {
  const res = await apiClient.get<OrgAggregate>(`/analytics/org/${orgId}/aggregate`);
  return res.data;
}
